/**
 * FilterHistory Component
 * Display and restore previous filter configurations
 */

import React from 'react';
import { History, X, RotateCcw } from 'lucide-react';
import { useFilterHistory } from '../../hooks/useFilterHistory';
import type { OKRFilters } from '../../types/okr';

interface FilterHistoryProps {
  onRestoreFilters: (filters: OKRFilters) => void;
}

export const FilterHistory: React.FC<FilterHistoryProps> = ({ onRestoreFilters }) => {
  const { history, removeFromHistory, clearHistory } = useFilterHistory();

  const formatTimestamp = (timestamp: number): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {
      return 'Just now';
    } else if (diffMins < 60) {
      return `${diffMins}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  if (history.length === 0) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-stone-600">
          <History className="w-4 h-4" />
          <h3 className="text-sm font-semibold">Filter History</h3>
        </div>

        <div className="text-center py-6 text-sm text-stone-500">
          Your filter history will appear here as you use filters.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-stone-600">
          <History className="w-4 h-4" />
          <h3 className="text-sm font-semibold">Filter History</h3>
        </div>
        {history.length > 0 && (
          <button
            onClick={clearHistory}
            className="flex items-center gap-1 px-2 py-1 text-xs text-danger-600 hover:bg-danger-50 rounded transition-colors"
            title="Clear all history"
          >
            <RotateCcw className="w-3 h-3" />
            Clear All
          </button>
        )}
      </div>

      <div className="space-y-2 max-h-96 overflow-y-auto">
        {history.map((item) => (
          <div
            key={item.id}
            className="group flex items-start gap-2 p-3 bg-white border border-stone-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-all"
          >
            <button
              onClick={() => onRestoreFilters(item.filters)}
              className="flex-1 text-left"
            >
              <div className="text-sm font-medium text-stone-900 group-hover:text-primary-700 line-clamp-2">
                {item.label}
              </div>
              <div className="text-xs text-stone-500 mt-1">
                {formatTimestamp(item.timestamp)}
              </div>
            </button>

            <button
              onClick={() => removeFromHistory(item.id)}
              className="p-1 text-stone-400 hover:text-danger-600 hover:bg-danger-50 rounded transition-colors opacity-0 group-hover:opacity-100"
              title="Remove from history"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      <p className="text-xs text-stone-500 italic">
        Click any item to restore those filters. History is stored locally.
      </p>
    </div>
  );
};
